import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { AREA_OF_NEED_LABELS } from '../../../lib/apdrSchemas';

/** UK school year assumption for annual cost estimates (shared across UI). */
export const SCHOOL_WEEKS_PER_YEAR = 39;
export const WEEKS_PER_TERM = 13;

export function annualCostFromWeekly(weeklyCost) {
  return Number(weeklyCost || 0) * SCHOOL_WEEKS_PER_YEAR;
}

/** Prefer stored weekly cost; derive annual using the shared school-weeks assumption. */
export function resolveProvisionCostDisplay(provision, staffMember) {
  const computed = computeStudentProvisionCosts(provision, staffMember);
  const weekly =
    provision?.weeklyCost > 0 ? Number(provision.weeklyCost) : Number(computed.weeklyCost || 0);
  const annual =
    weekly > 0 ? annualCostFromWeekly(weekly) : Number(computed.totalAnnual || 0);
  return { weekly, annual };
}

export function frequencyToSessions(freq) {
  const f = String(freq || '').toLowerCase();
  if (f === 'daily') return 5;
  if (f === 'weekly') return 1;
  if (f === 'biweekly') return 0.5;
  if (f === 'monthly') return 0.25;
  if (f.includes('as needed')) return 1;
  if (f.includes('2x')) return 2;
  if (f.includes('3x')) return 3;
  if (f.includes('5x')) return 5;
  return 1;
}

export function parseDurationToMinutes(duration) {
  if (!duration) return 30;
  const s = String(duration).toLowerCase();
  if (s.includes('per lesson')) return 50;
  if (hMatchOr(s)) {
    const hMatch = s.match(/(\d+)\s*h/);
    const mMatch = s.match(/(\d+)\s*m/);
    const hours = hMatch ? parseInt(hMatch[1], 10) : 0;
    const mins = mMatch ? parseInt(mMatch[1], 10) : 0;
    const total = (Number.isNaN(hours) ? 0 : hours) * 60 + (Number.isNaN(mins) ? 0 : mins);
    return total || 30;
  }
  const firstNum = s.match(/(\d+)/);
  return firstNum ? parseInt(firstNum[1], 10) : 30;
}

function hMatchOr(s) {
  return s.match(/(\d+)\s*h/) || s.match(/(\d+)\s*m/);
}

export function computeWeeklyCost(assignment, staffMember) {
  if (assignment?.weeklyCost > 0) return Number(assignment.weeklyCost);
  if (!assignment?.staffId || !staffMember) return 0;
  const hourly = Number(staffMember.hourlyRate || 0);
  const mins = Number(assignment.minutesPerSession || 0);
  const sessions = Number(assignment.sessionsPerWeek || 0);
  return (hourly / 60) * mins * sessions;
}

/** Compute weekly + annual cost for a student provision entry */
export function computeStudentProvisionCosts(provision, staffMember) {
  const provisionType = provision.provisionType || 'other';
  if (provisionType === 'equipment' || provisionType === 'other') {
    const annual = Number(provision.resourceCostAnnual || 0);
    return { weeklyCost: annual / SCHOOL_WEEKS_PER_YEAR, totalAnnual: annual };
  }

  const sessionsPerWeek = frequencyToSessions(provision.frequency);
  const minutesPerSession = parseDurationToMinutes(provision.duration);
  const weeklyCost = computeWeeklyCost(
    { staffId: provision.staffId, sessionsPerWeek, minutesPerSession },
    staffMember
  );
  return { weeklyCost, totalAnnual: weeklyCost * SCHOOL_WEEKS_PER_YEAR };
}

export function provisionToCostedRow(provision, staffMember) {
  const { weeklyCost, totalAnnual } = computeStudentProvisionCosts(provision, staffMember);
  const sessionsPerWeek = frequencyToSessions(provision.frequency);
  return {
    areaOfNeed: AREA_OF_NEED_LABELS[provision.areaOfNeed] || provision.areaOfNeed || 'SEN Support',
    provisionName: provision.name,
    provisionType: provision.provisionType || 'staff',
    staffId: provision.staffId || undefined,
    frequency: provision.frequency,
    duration: provision.duration,
    sessionsPerWeek,
    costPerSession: sessionsPerWeek > 0 ? weeklyCost / sessionsPerWeek : 0,
    costPerTermPerPerson: weeklyCost * WEEKS_PER_TERM,
    totalAnnual,
    source: 'studentProvision',
    sourceId: provision.id,
  };
}

export function interventionToCostedRow(intervention, assignment, staffMember) {
  const staffId = assignment.staffId || intervention.leadStaffId || intervention.staffLead || null;
  const sessionsPerWeek = Number(
    assignment.sessionsPerWeek || frequencyToSessions(intervention.frequency)
  );
  const minutesPerSession = Number(
    assignment.minutesPerSession || parseDurationToMinutes(intervention.duration)
  );
  const weeklyCost = computeWeeklyCost({ ...assignment, staffId, sessionsPerWeek, minutesPerSession }, staffMember);
  const totalAnnual = weeklyCost * SCHOOL_WEEKS_PER_YEAR;

  return {
    areaOfNeed: intervention.interventionType || intervention.targetedNeeds?.[0] || 'SEN Support',
    provisionName: intervention.name || 'Intervention',
    provisionType: 'staff',
    staffId: staffId || undefined,
    frequency: intervention.frequency || `${sessionsPerWeek}x weekly`,
    duration: intervention.duration || `${minutesPerSession} minutes`,
    sessionsPerWeek,
    costPerSession: sessionsPerWeek > 0 ? weeklyCost / sessionsPerWeek : 0,
    costPerTermPerPerson: weeklyCost * WEEKS_PER_TERM,
    totalAnnual,
    source: 'intervention',
    sourceId: intervention.id,
  };
}

export function formatCostedProvisionSummary(rows, totalAnnualCost) {
  if (!rows.length) return '';
  const lines = rows.map((row) => {
    const annual = Number(row.totalAnnual || 0).toFixed(2);
    return `• ${row.provisionName} (${row.frequency || 'weekly'}): £${annual}/year`;
  });
  lines.push('');
  lines.push(`Total estimated annual cost: £${Number(totalAnnualCost || 0).toFixed(2)}`);
  lines.push('(Based on staff hourly rates and provision schedules.)');
  return lines.join('\n');
}

/**
 * Load interventions + student provisions + staff for a student and build costed provision rows.
 */
export async function fetchCostedProvisionForStudent({ studentId, schoolId }) {
  if (!studentId || !schoolId) {
    return { rows: [], totalAnnualCost: 0, summary: '' };
  }

  const [intSnap, asnSnap, provSnap, staffSnap] = await Promise.all([
    getDocs(query(collection(db, 'interventions'), where('schoolId', '==', schoolId), where('students', 'array-contains', studentId))),
    getDocs(query(collection(db, 'interventionAssignments'), where('schoolId', '==', schoolId), where('studentId', '==', studentId))),
    getDocs(query(collection(db, 'studentProvisions'), where('schoolId', '==', schoolId), where('studentId', '==', studentId), where('status', '==', 'active'))),
    getDocs(query(collection(db, 'staff'), where('schoolId', '==', schoolId))),
  ]);

  const interventions = intSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const assignments = asnSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const provisions = provSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const staff = staffSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const staffById = Object.fromEntries(staff.map((s) => [s.id, s]));
  const assignmentByIntervention = Object.fromEntries(assignments.map((a) => [a.interventionId, a]));

  const interventionRows = interventions.map((intervention) => {
    const assignment = assignmentByIntervention[intervention.id] || {};
    const staffId = assignment.staffId || intervention.leadStaffId || null;
    return interventionToCostedRow(intervention, assignment, staffId ? staffById[staffId] : null);
  });

  const provisionRows = provisions.map((p) => {
    const staffMember = p.staffId ? staffById[p.staffId] : null;
    return provisionToCostedRow(p, staffMember);
  });

  const rows = [...interventionRows, ...provisionRows];
  const totalAnnualCost = rows.reduce((sum, row) => sum + Number(row.totalAnnual || 0), 0);
  return {
    rows,
    totalAnnualCost,
    summary: formatCostedProvisionSummary(rows, totalAnnualCost),
  };
}

/** Parent-facing provision list — no costs */
export async function fetchProvisionDetailForStudent({ studentId, schoolId }) {
  if (!studentId || !schoolId) return [];

  const [provSnap, staffSnap] = await Promise.all([
    getDocs(query(collection(db, 'studentProvisions'), where('schoolId', '==', schoolId), where('studentId', '==', studentId), where('status', '==', 'active'))),
    getDocs(query(collection(db, 'staff'), where('schoolId', '==', schoolId))),
  ]);

  const staffById = Object.fromEntries(staffSnap.docs.map((d) => [d.id, d.data()]));
  return provSnap.docs.map((d) => {
    const p = { id: d.id, ...d.data() };
    const staff = p.staffId ? staffById[p.staffId] : null;
    return {
      ...p,
      staffDisplayName: staff ? [staff.firstName, staff.lastName].filter(Boolean).join(' ') || staff.name : p.staffRoleLabel || '',
    };
  });
}
